import { useEffect, useRef } from "react";
import {ShieldCheck, Search, MessageCircleDashed as MessageCircle, Clock, TrendingUp, Link2} from 'lucide-react';

const items = [
{
  icon: ShieldCheck,
  title: "Mais credibilidade",
  desc: "Novos clientes confiam mais em quem tem um site profissional.",
  gradient: "from-blue-500 to-indigo-600",
  bg: "bg-blue-50"
},
{
  icon: Search,
  title: "Apareça no Google",
  desc: "Pessoas encontram seu negócio quando pesquisam na internet.",
  gradient: "from-emerald-500 to-teal-600",
  bg: "bg-emerald-50"
},
{
  icon: MessageCircle,
  title: "Mais contatos no WhatsApp",
  desc: "Seu site direciona clientes direto para o WhatsApp.",
  gradient: "from-green-500 to-emerald-600",
  bg: "bg-green-50"
},
{
  icon: Clock,
  title: "Aberto 24h por dia",
  desc: "Seu negócio disponível a qualquer hora, todos os dias.",
  gradient: "from-amber-500 to-orange-600",
  bg: "bg-amber-50"
},
{
  icon: TrendingUp,
  title: "Imagem profissional",
  desc: "Transmita seriedade e profissionalismo para seus clientes.",
  gradient: "from-rose-500 to-pink-600",
  bg: "bg-rose-50"
},
{
  icon: Link2,
  title: "Tudo centralizado",
  desc: "Redes sociais, endereço, telefone e informações em um só lugar.",
  gradient: "from-violet-500 to-purple-600",
  bg: "bg-violet-50"
}];


export default function Vantagens() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".fade-up").forEach((el) => {
              el.classList.add("visible");
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="vantagens" ref={sectionRef} className="md:py-14 bg-white relative overflow-hidden pt-[20px] pb-[20px]">
      {/* Decoração sutil */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-50 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-emerald-50 rounded-full blur-3xl opacity-40 translate-y-1/2 -translate-x-1/2" />

      <div className="max-w-6xl mx-auto px-5 relative z-10">
        {/* Cabeçalho — layout horizontal */}
        <div className="fade-up flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-500/10 rounded-full px-4 py-1.5 mb-4">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
              <span className="text-blue-600 text-xs font-bold tracking-wider uppercase">
                Vantagens
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight">
              Por que seu negócio
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                precisa de um site?
              </span>
            </h2>
          </div>
          <p className="text-gray-500 text-base md:text-lg max-w-sm leading-relaxed">
            Descubra como um site profissional transforma a presença do seu negócio.
          </p>
        </div>

        {/* Grid de vantagens — novo design com barra lateral colorida */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) =>
          <div
            key={item.title}
            className={`fade-up fade-up-delay-${Math.min(i + 1, 5)} group relative bg-white border border-gray-100 rounded-2xl p-5 transition-all duration-300 hover:shadow-xl hover:shadow-gray-100 hover:-translate-y-1 overflow-hidden`}>

              {/* Barra lateral colorida */}
              <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${item.gradient} rounded-l-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${item.gradient} flex items-center justify-center flex-shrink-0 shadow-lg shadow-gray-200/50`}>
                  <item.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-gray-900 mb-0.5">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Frase de reforço */}
        <div className="fade-up mt-8 text-center">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl px-8 py-5 shadow-xl shadow-slate-900/10">
            <span className="text-2xl">💡</span>
            <p className="text-white font-semibold text-base md:text-lg">
              Hoje, quem não tem site, perde oportunidades.
            </p>
          </div>
        </div>
      </div>
    </section>);

}